import { Injectable } from "@angular/core";
import { PatternCategories } from "../config";
import { mergeObject } from "../util";
import { ContentData } from "./content.data";

export interface TranslationStore {
    // Key is namespace.
    [key: string]: namespaceItem;
}

export interface namespaceItem {
    // Key is locale, value is detail messages.
    availableTranslations: { [key: string]: {} };
}

export interface PatternStore {
    [locale: string]: {
        // Key is category name.
        availablePatterns: { [key: string]: any };
    };
}

export abstract class I18nResource {
    public translation: TranslationStore = {};
    protected pattern: PatternStore = {};
    protected sharedNameSpaceList: Set<string> = new Set<string>();
    abstract nameSpaceList: Array<string>;
    abstract addTranslation(namespace: string, locale: string, data: { [key: string]: string | ContentData }): void;
    abstract getTranslation(namespace: string, locale: string): any;
    abstract getSharedTranslation(locale: string): any[];
    abstract registerSharedNamespace(namespace: string): any;
    abstract isTanslationExist(namespace: string, locale: string): boolean;
    abstract addPattern(locale: string, data: any): void;
    abstract getPattern(locale: string): any;
    abstract isPatternExist(locale: string, categories: PatternCategories): boolean;
    abstract filterScope(locale: string, categories: Array<PatternCategories>): Array<PatternCategories>;
}

@Injectable({
    providedIn: "root",
})
export class DefaultI18nResource extends I18nResource {
    constructor() {
        super();
    }

    /**
     * Get pattern by locale.
     * @param locale The locale tag.
     * @returns Loaded i18n data of supported categories.
     */
    public getPattern(locale: string) {
        return this.pattern[locale] && this.pattern[locale].availablePatterns;
    }

    get nameSpaceList() {
        return Object.keys(this.translation);
    }

    /**
     * Get all shared translations.
     * @param locale The locale tag.
     */
    public getSharedTranslation(locale: string) {
        let translations: any[] = [];
        this.sharedNameSpaceList.forEach((namespace) => {
            if (this.translation[namespace] && this.translation[namespace].availableTranslations[locale]) {
                translations.push(this.translation[namespace].availableTranslations[locale]);
            }
        });
        return translations;
    }

    /**
     * Register shared namespace.
     * @param namespace The unique identification for a configuration.
     */
    public registerSharedNamespace(namespace: string) {
        if (namespace) {
            this.sharedNameSpaceList.add(namespace);
        }
    }

    /**
     * Add translations to the corresponding lcoale and namespace.
     * @param namespace The unique identification for a configuration.
     * @param locale The locale tag.
     * @param data The tanslation data to be added.
     */
    public addTranslation(namespace: string, locale: string, data: any) {
        if (!this.translation[namespace]) {
            this.translation[namespace] = { availableTranslations: { [locale]: data } };
        } else {
            const existData = this.translation[namespace].availableTranslations[locale];
            this.translation[namespace].availableTranslations[locale] = mergeObject(existData, data);
        }
    }

    /**
     * Add patterns to the corresponding locale.
     * @param locale The locale tag.
     * @param data The pattern data to be added.
     */
    public addPattern(locale: string, data: any) {
        if (!this.pattern[locale]) {
            this.pattern[locale] = {
                availablePatterns: {},
            };
        }
        const existData = this.pattern[locale].availablePatterns;
        this.pattern[locale].availablePatterns = mergeObject(existData, data) as any;

        if (data.supplemental) {
            // Only new data nodes will be added, and the same data nodes remain unchanged.
            const supplemental = mergeObject(existData.supplemental, data.supplemental);
            this.pattern[locale].availablePatterns.supplemental = supplemental;
        }
    }

    /**
     * Get translations by namespace and lcoale.
     * @param namespace The unique identification for a configuration.
     * @param locale The locale tag.
     * @returns The translations under namespace and locale.
     */
    public getTranslation(namespace: string, locale: string) {
        return this.translation[namespace] && this.translation[namespace].availableTranslations[locale];
    }

    /**
     * Check translation status for the given namespace and lcoale.
     * @param namespace The unique identification for a configuration.
     * @param locale The locale tag.
     * @returns True or False to indicate the translations are there.
     */
    public isTanslationExist(namespace: string, locale: string): boolean {
        return this.translation[namespace] && this.translation[namespace].availableTranslations[locale] ? true : false;
    }

    /**
     * Check pattern status for the given namespace and lcoale.
     * @param namespace The unique identification for a configuration.
     * @param locale The locale tag.
     * @returns True or False to indicate the patterns are there.
     */
    public isPatternExist(locale: string, categories: PatternCategories): boolean {
        return this.pattern[locale] && this.pattern[locale].availablePatterns[categories] ? true : false;
    }

    /**
     * Filter the i18n scope to get the missing pattern category list.
     * @param locale The locale tag.
     * @param categories The pattern categories.
     * @returns A list of categories that have no corresponding data in the pattern data store.
     */
    public filterScope(locale: string, categories: Array<PatternCategories>) {
        let list: Array<PatternCategories> = [];
        if (this.pattern[locale]) {
            const existScope = Object.keys(this.pattern[locale].availablePatterns);
            categories.forEach((element: PatternCategories) => {
                if (existScope.indexOf(element) < 0) {
                    list.push(element);
                }
            });
        } else {
            list = categories;
        }
        return list;
    }
}
